import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useToast } from '../../hooks/useToast';
import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
  Button,
  IconButton,
  OutlinedInput,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Tooltip,
  Box,
  Pagination,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Divider
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AdminWalletCredit from './AdminWalletCredit';
import CloseIcon from '@mui/icons-material/Close';

const ROLE_META = {
  SuperAdmin: { palette: 'error', label: 'Super Admin' },
  Admin: { palette: 'primary', label: 'Admin' },
  Moderator: { palette: 'info', label: 'Moderator' },
  Manager: { palette: 'success', label: 'Manager' },
  Support: { palette: 'warning', label: 'Support' },
  Observer: { palette: 'secondary', label: 'Observer' },
  User: { palette: 'secondary', label: 'User' },
};

const STATUS_META = {
  active: { palette: 'success', label: 'Active' },
  inactive: { palette: 'default', label: 'Inactive' },
  banned: { palette: 'error', label: 'Banned' },
  suspended: { palette: 'warning', label: 'Suspended' },
};

const AdminUsers = ({
  users,
  usersLoading,
  usersError,
  usersPage,
  usersTotalPages,
  usersSearch,
  setUsersSearch,
  showUserModal,
  editingUser,
  userForm,
  userFormError,
  userFormLoading,
  openUserModal,
  closeUserModal,
  handleUserFormSubmit,
  handleUserFormChange,
  handleDeleteUser,
  fetchUsers
}) => {
  const [walletUser, setWalletUser] = useState(null);
  const theme = useTheme();
  const toast = useToast();
  const usersList = users || [];

  const handleExportCSV = async () => {
    try {
      const res = await fetch('/api/users/export', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to export users');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed: ' + err.message);
    }
  };

  const handleSearchChange = (event) => {
    setUsersSearch(event.target.value);
  };

  const handlePaginationChange = (_, page) => {
    fetchUsers(page, usersSearch);
  };

  const getChipStyles = (paletteKey) => {
    if (!paletteKey || !theme.palette[paletteKey]) {
      return {
        bgcolor: alpha(theme.palette.text.primary, 0.06),
        color: theme.palette.text.primary,
        border: 'none',
        borderRadius: 2,
        fontWeight: 600,
      };
    }
    return {
      bgcolor: alpha(theme.palette[paletteKey].main, 0.12),
      color: theme.palette[paletteKey].main,
      border: 'none',
      borderRadius: 2,
      fontWeight: 600,
    };
  };

  const searchPlaceholder = useMemo(
    () => `${usersList.length || 0} result${usersList.length === 1 ? '' : 's'} shown`,
    [usersList.length]
  );

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
      <CardHeader
        title="Users & Roles Management"
        subheader="Manage, review and audit every admin and customer account"
        titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
      />
      {usersLoading && <LinearProgress color="primary" />}
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mb: 3 }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <OutlinedInput
            value={usersSearch}
            onChange={handleSearchChange}
            placeholder="Search users by name, email or role"
            fullWidth
            sx={{
              maxWidth: { md: 360 },
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
            }}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            }
          />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openUserModal()}
              sx={{ borderRadius: 2 }}
            >
              Add User
            </Button>
          </Stack>
        </Stack>

        {usersError && (
          <Alert severity="error" variant="outlined" sx={{ mb: 3 }}>
            {usersError}
          </Alert>
        )}

        <TableContainer component={Box} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!usersLoading && usersList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        No users found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Adjust your search or add a new user to get started.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {usersList.map((user) => {
                const roleMeta = ROLE_META[user.role] || { palette: 'default', label: user.role || 'Unknown' };
                const statusMeta = STATUS_META[user.status] || { palette: 'default', label: user.status || 'Unknown' };
                return (
                  <TableRow hover key={user._id}>
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontWeight: 600 }}>
                          {user.username?.charAt(0)?.toUpperCase() || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {user.username || 'Unnamed User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : 'No profile data'}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={roleMeta.label}
                        sx={getChipStyles(roleMeta.palette)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusMeta.label}
                        sx={getChipStyles(statusMeta.palette)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Edit user" arrow>
                          <IconButton color="primary" onClick={() => openUserModal(user)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Adjust wallet" arrow>
                          <IconButton color="success" onClick={() => setWalletUser(user)}>
                            <AccountBalanceWalletOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete user" arrow>
                          <IconButton color="error" onClick={() => handleDeleteUser(user)}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {searchPlaceholder}
          </Typography>
          {usersTotalPages > 1 && (
            <Pagination
              count={usersTotalPages}
              page={usersPage}
              onChange={handlePaginationChange}
              color="primary"
              variant="outlined"
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
            />
          )}
        </Stack>
      </CardContent>

      <AdminWalletCredit
        open={Boolean(walletUser)}
        user={walletUser}
        onClose={() => setWalletUser(null)}
        onDone={() => { if (typeof fetchUsers === 'function') fetchUsers(); }}
      />

      <Dialog open={Boolean(showUserModal)} onClose={closeUserModal} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {editingUser ? 'Edit User' : 'Add User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Update account credentials and access level
              </Typography>
            </Box>
            <IconButton edge="end" onClick={closeUserModal}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" id="admin-user-form" onSubmit={handleUserFormSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Username"
                  name="username"
                  value={userForm.username}
                  onChange={handleUserFormChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  type="email"
                  label="Email"
                  name="email"
                  value={userForm.email}
                  onChange={handleUserFormChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={userForm.firstName}
                  onChange={handleUserFormChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Last Name"
                  name="lastName"
                  value={userForm.lastName}
                  onChange={handleUserFormChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="user-role-label">Role</InputLabel>
                  <Select
                    labelId="user-role-label"
                    label="Role"
                    name="role"
                    value={userForm.role}
                    onChange={handleUserFormChange}
                  >
                    <MenuItem value="">Select a role...</MenuItem>
                    <MenuItem value="SuperAdmin">Super Admin</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="Moderator">Moderator</MenuItem>
                    <MenuItem value="Manager">Manager</MenuItem>
                    <MenuItem value="Support">Support</MenuItem>
                    <MenuItem value="Observer">Observer</MenuItem>
                    <MenuItem value="User">User</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="user-status-label">Status</InputLabel>
                  <Select
                    labelId="user-status-label"
                    label="Status"
                    name="status"
                    value={userForm.status}
                    onChange={handleUserFormChange}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="banned">Banned</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {userFormError && (
              <Alert severity="error" variant="outlined" sx={{ mt: 3 }}>
                {userFormError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeUserModal} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            form="admin-user-form"
            variant="contained"
            disabled={userFormLoading}
            sx={{ minWidth: 140, borderRadius: 2 }}
          >
            {userFormLoading ? <CircularProgress size={20} color="inherit" /> : editingUser ? 'Update User' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

AdminUsers.propTypes = {
  users: PropTypes.array,
  usersLoading: PropTypes.bool,
  usersError: PropTypes.string,
  usersPage: PropTypes.number,
  usersTotalPages: PropTypes.number,
  usersSearch: PropTypes.string,
  setUsersSearch: PropTypes.func.isRequired,
  showUserModal: PropTypes.bool,
  editingUser: PropTypes.object,
  userForm: PropTypes.object.isRequired,
  userFormError: PropTypes.string,
  userFormLoading: PropTypes.bool,
  openUserModal: PropTypes.func.isRequired,
  closeUserModal: PropTypes.func.isRequired,
  handleUserFormSubmit: PropTypes.func.isRequired,
  handleUserFormChange: PropTypes.func.isRequired,
  handleDeleteUser: PropTypes.func.isRequired,
  fetchUsers: PropTypes.func.isRequired,
};

export default AdminUsers;